import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import DataTable from '../data_table/DataTable';
import LimitTrack from './LimitTrack';
import RuleLimitInput from './RuleLimitInput';
import RuleStatusChip from './RuleStatusChip';
import { buildManualConstructionRows, constructionLimitCount } from './ManualConstructionRows';
import {
    EmptyScopeMessage,
    RULE_TABLE_CLASS_NAME,
    RULE_TABLE_HEADER_HEIGHT,
    RuleLabel,
    RuleValue,
    renderRuleFullWidthRow,
    ruleColumns,
    ruleModeControl,
    ruleNoteClassName,
} from './OptimizerRuleTableParts';
import { ruleEditState } from './optimizerRuleRegistry';

const SCOPE_LABELS = Object.freeze({
    portfolio: 'Portfolio',
    factor: 'Factor',
    attribute: 'Attribute',
});

function evaluationFor(entry, evaluationIndex) {
    return entry.scope === 'attribute'
        ? evaluationIndex.byGroupIntentKey.get(entry.id)
        : evaluationIndex.byRuleId.get(entry.id);
}

// Every enabled rule across scopes. Each row edits the same record its scope
// view edits; switching a rule off removes it from this list but keeps its limit.
export default function ActiveLimitsTable({
    activeRules,
    evaluationIndex,
    baselineLabel,
    actions,
    resultStale = false,
    disabled = false,
    construction = null,
    searchRanges = null,
    rangeEditor = null,
    modeEditor = null,
}) {
    const hasModeEditor = Boolean(modeEditor);
    const hasRangeEditor = Boolean(rangeEditor);
    const columns = useMemo(() => ruleColumns([
        { id: 'scope', header: 'Scope', width: 8, wrap: false },
        { id: 'rule', header: 'Rule', width: 20 },
        { id: 'mode', header: hasModeEditor ? 'Mode' : 'On', width: hasModeEditor ? 9 : 6, align: 'center' },
        {
            id: 'limit',
            header: hasRangeEditor ? 'Limit / range' : 'Limit',
            width: hasRangeEditor ? 24 : 15,
            align: 'right',
        },
        { id: 'baseline', header: baselineLabel, width: 11, align: 'right', wrap: false },
        { id: 'optimized', header: 'Optimized', width: 11, align: 'right', wrap: false, optimized: true },
        { id: 'track', header: 'Result vs limit', width: 17, align: 'right' },
        { id: 'status', header: 'Status', width: 12 },
    ], hasRangeEditor ? 1100 : 860), [baselineLabel, hasModeEditor, hasRangeEditor]);

    if (activeRules.length === 0 && !constructionLimitCount(construction?.constraints?.design)) {
        return <EmptyScopeMessage>No limits are switched on.</EmptyScopeMessage>;
    }
    const setValue = (entry, value) => {
        if (entry.scope === 'portfolio') actions.setPortfolioRuleValue(entry.rule.key, value);
        else if (entry.scope === 'factor') actions.setFactorRuleValue(entry.factor, value);
        else actions.setGroupRuleValue(entry.id, value);
    };
    const switchOff = (entry) => {
        if (entry.scope === 'portfolio') actions.setPortfolioRuleEnabled(entry.rule.key, false);
        else if (entry.scope === 'factor') actions.setFactorRuleEnabled(entry.factor, false);
        else actions.setGroupRuleEnabledByIdentity(entry.id, false);
    };
    const entryRow = (entry) => {
        const evaluation = evaluationFor(entry, evaluationIndex);
        const parameter = entry.scope === 'factor' ? `factor:${entry.factor}`
            : entry.scope === 'attribute' ? (evaluation?.rule_id ? `group:${evaluation.rule_id}` : null)
                : entry.id;
        const { changed, draftLimit } = ruleEditState(evaluation, {
            enabled: true,
            value: entry.value,
            explore: Boolean(searchRanges?.[parameter]),
        });
        const unitLabel = entry.scope === 'portfolio'
            ? entry.rule.unitLabel
            : `${entry.rule.label} · ${entry.rule.unitLabel}`;
        let mode = <span className={ruleNoteClassName}>Always</span>;
        if (!entry.rule.alwaysApplied) {
            mode = ruleModeControl(modeEditor, {
                parameter,
                enabled: true,
                value: entry.value,
                label: entry.label,
                disabled,
                onEnabledChange: (on) => { if (!on) switchOff(entry); },
            });
        }
        return {
            id: `${entry.scope}:${entry.id}`,
            cells: {
                scope: <span className="text-[0.7rem] text-content-secondary">{SCOPE_LABELS[entry.scope]}</span>,
                rule: (
                    <RuleLabel
                        label={entry.label}
                        unitLabel={unitLabel}
                        description={entry.rule.description ?? null}
                    />
                ),
                mode,
                limit: rangeEditor && !entry.rule.alwaysApplied ? rangeEditor({ parameter, value: entry.value,
                    label: entry.label, unit: entry.rule.unit, form: entry.rule.form, disabled,
                    onChange: (value) => setValue(entry, value),
                }) : (
                    <RuleLimitInput
                        value={entry.value}
                        onChange={(value) => setValue(entry, value)}
                        unit={entry.rule.unit}
                        form={entry.rule.form}
                        disabled={disabled}
                        ariaLabel={`${entry.label} limit`}
                        width={entry.rule.unit === 'usd' ? 150 : 118}
                    />
                ),
                baseline: <RuleValue value={evaluation?.baseline ?? null} unit={entry.rule.unit} />,
                optimized: <RuleValue value={evaluation?.optimized ?? null} unit={entry.rule.unit} />,
                track: (
                    <LimitTrack
                        evaluation={evaluation}
                        rule={entry.rule}
                        label={entry.label}
                        baselineLabel={baselineLabel}
                        draftLimit={draftLimit}
                    />
                ),
                status: (
                    <RuleStatusChip
                        evaluation={evaluation}
                        rule={entry.rule}
                        changed={changed}
                        stale={resultStale}
                    />
                ),
            },
        };
    };
    const rows = [
        ...(construction ? buildManualConstructionRows({
            ...construction, actions, evaluationIndex, baselineLabel, disabled, resultStale, rangeEditor, modeEditor, searchRanges, activeOnly: true,
        }) : []),
        ...activeRules.map(entryRow),
    ];

    return (
        <DataTable
            aria-label="Active limits"
            data={rows}
            columns={columns}
            renderFullWidthRow={renderRuleFullWidthRow}
            headerHeight={RULE_TABLE_HEADER_HEIGHT}
            wrapHeaders
            className={RULE_TABLE_CLASS_NAME}
        />
    );
}

ActiveLimitsTable.propTypes = {
    activeRules: PropTypes.arrayOf(PropTypes.shape({
        scope: PropTypes.oneOf(['portfolio', 'factor', 'attribute']).isRequired,
        id: PropTypes.string.isRequired,
        rule: PropTypes.object.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.number,
        factor: PropTypes.string,
    })).isRequired,
    evaluationIndex: PropTypes.shape({
        byRuleId: PropTypes.instanceOf(Map),
        byGroupIntentKey: PropTypes.instanceOf(Map),
    }).isRequired,
    baselineLabel: PropTypes.string.isRequired,
    actions: PropTypes.object.isRequired,
    resultStale: PropTypes.bool,
    disabled: PropTypes.bool,
};
